import { useState, useEffect, useRef } from "react";
import { DashboardLayout } from "../components/dashboard/dashboard-layout";
import ProtectedRoute from "../components/ProtectedRoute";
import { PageHeader } from "../components/dashboard/page-header";
import { AreaChart } from "../components/dashboard/area-chart";
import { DonutChart } from "../components/dashboard/donut-chart";
import { ProductChart } from "../components/dashboard/product-chart";
import {
  BarChart2,
  LineChart,
  PieChart,
  Download,
  Filter,
  ArrowUpRight,
  ArrowDownRight,
  MessageSquare,
  Clock,
  Star,
  TrendingUp,
  Users,
  Activity,
  RefreshCw,
  Wifi,
  WifiOff,
  Zap,
  Eye,
  Calendar,
  Target,
  Sparkles
} from "lucide-react";
import dashboardService from "../services/dashboard";
import { responsesService } from "../services/responsesService";
import exportService from "../services/exportService";
import type { RealDashboardStats } from "../services/dashboard";
import type { Comment } from "../services/responsesService";
import type { ExportData } from "../services/exportService";

import "../styles/animations.css";
import "../styles/custom-animations.css";

function AnalyticsContent() {
  const [activeTab, setActiveTab] = useState("overview");
  const [timeRange, setTimeRange] = useState("7d");
  const [dashboardStats, setDashboardStats] = useState<RealDashboardStats | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [productAnalytics, setProductAnalytics] = useState<any>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date().toLocaleDateString());
  const [selectedDateRange, setSelectedDateRange] = useState<{ from: Date; to?: Date } | undefined>(() => {
    // Default to last 7 days
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 6);
    return { from: start, to: end };
  });

  // Real-time dashboard state
  const [isRealTimeEnabled, setIsRealTimeEnabled] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [isConnected, setIsConnected] = useState(true);
  const [animateStats, setAnimateStats] = useState(false);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Chart data state
  const [chartData, setChartData] = useState({
    timeSeriesData: [] as any[],
    platformData: [] as any[],
    productData: [] as any[],
    trendData: [] as any[]
  });

  // Fetch real data on component mount and setup real-time updates
  useEffect(() => {
    fetchAnalyticsData();

    // Setup real-time updates every 30 seconds
    const interval = setInterval(() => {
      fetchAnalyticsData(true);
    }, 30000);

    refreshIntervalRef.current = interval;

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [timeRange]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showExportMenu) {
        setShowExportMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportMenu]);

  // Date picker handler
  const handleDateClick = () => {
    console.log('📅 Date picker clicked - could open date range selector');
    // For now, just update the current date display
    setCurrentDate(new Date().toLocaleDateString());
  };

  // Date range change handler
  const handleDateRangeChange = (range: { from: Date; to?: Date } | undefined) => {
    console.log('📅 Date range changed:', range);
    setSelectedDateRange(range);

    // Update timeRange based on selected range
    if (range?.from && range?.to) {
      const diffTime = Math.abs(range.to.getTime() - range.from.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays <= 1) {
        setTimeRange('24h');
      } else if (diffDays <= 7) {
        setTimeRange('7d');
      } else if (diffDays <= 30) {
        setTimeRange('30d');
      } else if (diffDays <= 90) {
        setTimeRange('90d');
      } else {
        setTimeRange('all');
      }
    }
  };

  // Simple, clean export function - rewritten from scratch
  const handleExport = (format: 'json' | 'csv' = 'csv') => {
    console.log('� Export started:', format);

    // Check data availability
    if (!dashboardStats || !productAnalytics) {
      alert('Please wait for data to load before exporting.');
      return;
    }

    try {
      // Generate content based on format
      let content = '';
      let filename = '';

      if (format === 'csv') {
        // Simple CSV generation
        const lines = [
          'Analytics Export Report',
          `Generated: ${new Date().toLocaleString()}`,
          `Time Range: ${timeRange}`,
          '',
          'SUMMARY',
          `Total Reviews,${dashboardStats.totalReviews || 0}`,
          `Response Rate,${dashboardStats.responseRate || 0}%`,
          `Average Rating,${dashboardStats.avgRating || 0}`,
          `Total Products,${productAnalytics.totalProducts || 0}`,
          '',
          'PRODUCTS',
          'Name,Reviews,Customers,Rating'
        ];

        // Add products
        if (productAnalytics.products) {
          productAnalytics.products.forEach((p: any) => {
            lines.push(`"${p.name || 'Unknown'}",${p.reviewCount || 0},${p.customerCount || 0},${p.avgRating || 0}`);
          });
        }

        content = lines.join('\n');
        filename = `analytics-${new Date().toISOString().split('T')[0]}.csv`;

      } else {
        // Simple JSON generation
        const data = {
          generated: new Date().toISOString(),
          timeRange: timeRange,
          summary: {
            totalReviews: dashboardStats.totalReviews || 0,
            responseRate: dashboardStats.responseRate || 0,
            avgRating: dashboardStats.avgRating || 0,
            totalProducts: productAnalytics.totalProducts || 0
          },
          products: productAnalytics.products || []
        };

        content = JSON.stringify(data, null, 2);
        filename = `analytics-${new Date().toISOString().split('T')[0]}.json`;
      }

      // Download using the exact same method as the working test
      const blob = new Blob([content], { type: format === 'csv' ? 'text/csv' : 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log('✅ Export successful:', filename);
      alert(`Export successful!\nFile: ${filename}\nCheck your Downloads folder.`);

    } catch (error) {
      console.error('❌ Export failed:', error);
      alert('Export failed. Please try again.');
    }
  };



  const fetchAnalyticsData = async (isRealTimeUpdate = false) => {
    try {
      if (!isRealTimeUpdate) {
        setIsLoading(true);
      }
      setError(null);

      console.log('🔄 Fetching analytics data...', { isRealTimeUpdate });

      // Fetch dashboard stats and comments in parallel
      const [stats, commentsData] = await Promise.all([
        dashboardService.getRealDashboardStats(),
        responsesService.fetchComments()
      ]);

      setDashboardStats(stats);
      setComments(commentsData);

      // Calculate product analytics from comments
      const productStats = calculateProductAnalytics(commentsData);
      setProductAnalytics(productStats);

      // Generate chart data
      const newChartData = generateChartData(commentsData, stats);
      setChartData(newChartData);

      // Update real-time state
      setLastUpdate(new Date());
      setIsConnected(true);

      if (!isRealTimeUpdate) {
        setAnimateStats(true);
      }

      console.log('✅ Analytics data updated successfully');

    } catch (err: any) {
      console.error('❌ Error fetching analytics data:', err);
      setError(err.message || 'Failed to load analytics data');
      setIsConnected(false);
    } finally {
      if (!isRealTimeUpdate) {
        setIsLoading(false);
      }
    }
  };

  // Generate chart data from comments and stats
  const generateChartData = (commentsData: Comment[], stats: RealDashboardStats) => {
    // Time series data for the last 30 days
    const timeSeriesData = generateTimeSeriesData(commentsData);

    // Platform distribution data
    const platformData = generatePlatformData(stats.platformBreakdown || {});

    // Product performance data
    const productData = generateProductData(commentsData);

    // Trend data for multi-line chart
    const trendData = generateTrendData(commentsData);

    return {
      timeSeriesData,
      platformData,
      productData,
      trendData
    };
  };

  // Generate time series data for area chart
  const generateTimeSeriesData = (commentsData: Comment[]) => {
    const last30Days = Array.from({ length: 30 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (29 - i));
      return date;
    });

    return last30Days.map(date => {
      const dayComments = commentsData.filter(comment => {
        const commentDate = new Date(comment.createdAt || comment.time || '');
        return commentDate.toDateString() === date.toDateString();
      });

      return {
        time: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        value: dayComments.length,
        label: `${dayComments.length} reviews`
      };
    });
  };

  // Generate platform distribution data for donut chart
  const generatePlatformData = (platformBreakdown: any) => {
    const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

    return Object.entries(platformBreakdown).map(([platform, data]: [string, any], index) => ({
      label: platform.charAt(0).toUpperCase() + platform.slice(1),
      value: data.total || data.count || 0,
      color: colors[index % colors.length]
    }));
  };

  // Generate product performance data for bar chart
  const generateProductData = (commentsData: Comment[]) => {
    const productMap = new Map();

    commentsData.forEach(comment => {
      const productName = comment.product_title || 'Unknown';
      if (!productMap.has(productName)) {
        productMap.set(productName, 0);
      }
      productMap.set(productName, productMap.get(productName) + 1);
    });

    return Array.from(productMap.entries())
      .map(([name, count]) => ({
        label: name.length > 20 ? name.substring(0, 20) + '...' : name,
        value: count as number,
        color: '#3B82F6'
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  };

  // Generate trend data for multi-line chart
  const generateTrendData = (commentsData: Comment[]) => {
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (6 - i));
      return date;
    });

    const series = [
      {
        name: 'Reviews',
        color: '#3B82F6',
        data: last7Days.map(date => {
          const dayComments = commentsData.filter(comment => {
            const commentDate = new Date(comment.createdAt || comment.time || '');
            return commentDate.toDateString() === date.toDateString();
          });
          return {
            time: date.toLocaleDateString('en-US', { weekday: 'short' }),
            value: dayComments.length
          };
        })
      },
      {
        name: 'Responses',
        color: '#10B981',
        data: last7Days.map(date => {
          const dayResponses = commentsData.filter(comment => {
            const commentDate = new Date(comment.createdAt || comment.time || '');
            return commentDate.toDateString() === date.toDateString() &&
                   (comment.status === 'sent' || comment.status === 'draft');
          });
          return {
            time: date.toLocaleDateString('en-US', { weekday: 'short' }),
            value: dayResponses.length
          };
        })
      }
    ];

    return series;
  };

  // Calculate product-focused analytics
  const calculateProductAnalytics = (comments: Comment[]) => {
    const productMap = new Map();

    // Clean product names - filter out non-product entries
    const cleanProductName = (name: string) => {
      if (!name || name.trim() === '') return 'Unknown Product';

      // Filter out obvious non-product entries
      const nonProducts = [
        'running out of space',
        'work mode on its intense',
        'never',
        'platforms',
        'introducing',
        'these are not products'
      ];

      const lowerName = name.toLowerCase();
      if (nonProducts.some(nonProd => lowerName.includes(nonProd))) {
        return null; // Skip this entry
      }

      // Clean and format product names
      return name.trim()
        .replace(/^\d+\s*/, '') // Remove leading numbers
        .replace(/\s+/g, ' ') // Normalize spaces
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    };

    comments.forEach(comment => {
      const cleanedName = cleanProductName(comment.product_title || '');
      if (!cleanedName) return; // Skip non-product entries

      if (!productMap.has(cleanedName)) {
        productMap.set(cleanedName, {
          name: cleanedName,
          reviewCount: 0,
          totalRating: 0,
          ratingCount: 0,
          avgRating: 0,
          responseCount: 0,
          avgResponseTime: 0,
          totalResponseTime: 0,
          platforms: new Set(),
          customers: new Set(),
          recentReviews: 0,
          sentimentScore: 0,
          totalSentiment: 0
        });
      }

      const product = productMap.get(cleanedName);
      product.reviewCount++;
      product.platforms.add(comment.platform);
      product.customers.add(comment.email || comment.reviewer_display_name);

      // Rating calculation
      if (comment.rating && !isNaN(Number(comment.rating))) {
        product.totalRating += Number(comment.rating);
        product.ratingCount++;
        product.avgRating = product.totalRating / product.ratingCount;
      }

      // Response time calculation
      if (comment.responseTimeHours && comment.status === 'sent') {
        product.responseCount++;
        product.totalResponseTime += comment.responseTimeHours;
        product.avgResponseTime = product.totalResponseTime / product.responseCount;
      }

      // Sentiment analysis (basic)
      const content = comment.content?.toLowerCase() || '';
      let sentiment = 0;
      if (content.includes('love') || content.includes('great') || content.includes('excellent')) sentiment += 2;
      if (content.includes('good') || content.includes('nice') || content.includes('happy')) sentiment += 1;
      if (content.includes('bad') || content.includes('terrible') || content.includes('hate')) sentiment -= 2;
      if (content.includes('poor') || content.includes('disappointed')) sentiment -= 1;

      product.totalSentiment += sentiment;
      product.sentimentScore = product.totalSentiment / product.reviewCount;

      // Recent reviews (last 7 days)
      const commentDate = new Date(comment.createdAt || comment.time || Date.now());
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      if (commentDate >= sevenDaysAgo) {
        product.recentReviews++;
      }
    });

    // Convert to array and sort by review count
    const products = Array.from(productMap.values())
      .map(product => ({
        ...product,
        platformCount: product.platforms.size,
        customerCount: product.customers.size,
        platforms: Array.from(product.platforms),
        avgRating: Math.round(product.avgRating * 10) / 10,
        avgResponseTime: Math.round(product.avgResponseTime * 10) / 10
      }))
      .sort((a, b) => b.reviewCount - a.reviewCount);

    return {
      products: products.slice(0, 15), // Top 15 products
      allProducts: products,
      totalProducts: products.length,
      totalReviews: comments.length,
      avgProductRating: products.reduce((sum, p) => sum + p.avgRating, 0) / products.length || 0,
      topPerformers: products.filter(p => p.avgRating >= 4.5).slice(0, 5),
      needsAttention: products.filter(p => p.avgRating < 3.5 && p.reviewCount > 2).slice(0, 5)
    };
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Product Analytics"
        description="Comprehensive insights into your product performance and customer engagement"
        tabs={[
          { id: "overview", label: "Overview" },
          { id: "products", label: "Products" },
          { id: "engagement", label: "Engagement" },
          { id: "performance", label: "Performance" },
        ]}
        defaultTab="overview"
        onTabChange={(tabId) => setActiveTab(tabId)}
        onRefresh={fetchAnalyticsData}
        showDatePicker={true}
        selectedDateRange={selectedDateRange}
        onDateRangeChange={handleDateRangeChange}
      />

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
            <RefreshCw className="h-5 w-5 animate-spin" />
            <span>Loading analytics data...</span>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
            <span className="font-medium">Error loading analytics:</span>
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Real-time Status Bar */}
      {!isLoading && (
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-4 mb-6 smooth-fadeIn">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                isConnected
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
                  : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
              }`}>
                {isConnected ? (
                  <>
                    <Wifi className="h-4 w-4" />
                    <span>Live Data</span>
                  </>
                ) : (
                  <>
                    <WifiOff className="h-4 w-4" />
                    <span>Disconnected</span>
                  </>
                )}
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <Activity className="h-4 w-4" />
                <span>Last updated: {lastUpdate.toLocaleTimeString()}</span>
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <Sparkles className="h-4 w-4" />
                <span>Auto-refresh: 30s</span>
              </div>
            </div>

            <button
              onClick={() => fetchAnalyticsData(false)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 smooth-btn-transition"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Refresh Now</span>
            </button>
          </div>
        </div>
      )}

      {/* Time Range Selector */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <button
            className={`px-3 py-1.5 text-sm rounded-lg transition-all duration-200 ${timeRange === '24h' ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'}`}
            onClick={() => setTimeRange('24h')}
          >
            24h
          </button>
          <button
            className={`px-3 py-1.5 text-sm rounded-lg transition-all duration-200 ${timeRange === '7d' ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'}`}
            onClick={() => setTimeRange('7d')}
          >
            7d
          </button>
          <button
            className={`px-3 py-1.5 text-sm rounded-lg transition-all duration-200 ${timeRange === '30d' ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'}`}
            onClick={() => setTimeRange('30d')}
          >
            30d
          </button>
          <button
            className={`px-3 py-1.5 text-sm rounded-lg transition-all duration-200 ${timeRange === '90d' ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'}`}
            onClick={() => setTimeRange('90d')}
          >
            90d
          </button>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              onClick={() => {
                console.log('🖱️ Export button clicked!');
                console.log('📊 Current state:', {
                  isLoading,
                  hasDashboardStats: !!dashboardStats,
                  hasProductAnalytics: !!productAnalytics,
                  showExportMenu
                });
                setShowExportMenu(!showExportMenu);
                console.log('📋 Export menu toggled to:', !showExportMenu);
              }}
              disabled={isLoading || !dashboardStats || !productAnalytics}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="h-4 w-4" />
              <span>Export</span>
            </button>

            {showExportMenu && !isLoading && dashboardStats && productAnalytics && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                <div className="py-1">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('🖱️ CSV export clicked from dropdown!');
                      handleExport('csv');
                      setShowExportMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Export as CSV
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('🖱️ JSON export clicked from dropdown!');
                      handleExport('json');
                      setShowExportMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Export as JSON
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Enhanced Real-time Analytics Stats Cards */}
      {!isLoading && dashboardStats && (activeTab === "overview" || activeTab === "products") && (
        <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 mb-6 md:mb-8">
          <div className={`bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 hover:shadow-md transition-all duration-300 p-5 smooth-card-hover ${animateStats ? 'smooth-fadeIn smooth-delay-100' : 'opacity-0'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Customer Reviews</h3>
              <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 hover-rotate">
                <MessageSquare className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </div>
            </div>
            <div className="flex items-baseline">
              <span className="text-3xl font-bold text-gray-900 dark:text-white animate-counter">
                {dashboardStats.totalReviews.toLocaleString()}
              </span>
              <span className="ml-2 text-sm font-medium flex items-center text-green-600 dark:text-green-400">
                <ArrowUpRight className="h-3 w-3 mr-1" />
                +{Math.floor(dashboardStats.totalReviews * 0.15)}%
              </span>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                <div
                  className="bg-blue-600 h-1.5 rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${Math.min(100, (dashboardStats.totalReviews / 100) * 100)}%` }}
                ></div>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                vs last month
              </span>
            </div>
          </div>

          <div className={`bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 hover:shadow-md transition-all duration-300 p-5 smooth-card-hover ${animateStats ? 'smooth-fadeIn smooth-delay-200' : 'opacity-0'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Customer Engagement</h3>
              <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 hover-rotate">
                <Activity className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </div>
            </div>
            <div className="flex items-baseline">
              <span className="text-3xl font-bold text-gray-900 dark:text-white animate-counter">
                {dashboardStats.responseRate}%
              </span>
              <span className="ml-2 text-sm font-medium flex items-center text-green-600 dark:text-green-400">
                <ArrowUpRight className="h-3 w-3 mr-1" />
                +{Math.floor(dashboardStats.responseRate * 0.08)}%
              </span>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                <div
                  className="bg-green-600 h-1.5 rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${dashboardStats.responseRate}%` }}
                ></div>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                response rate
              </span>
            </div>
          </div>

          <div className={`bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 hover:shadow-md transition-all duration-300 p-5 smooth-card-hover ${animateStats ? 'smooth-fadeIn smooth-delay-300' : 'opacity-0'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Response Speed</h3>
              <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 hover-rotate">
                <Clock className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </div>
            </div>
            <div className="flex items-baseline">
              <span className="text-3xl font-bold text-gray-900 dark:text-white animate-counter">
                {dashboardStats.avgResponseTime}h
              </span>
              <span className="ml-2 text-sm font-medium flex items-center text-green-600 dark:text-green-400">
                <ArrowDownRight className="h-3 w-3 mr-1" />
                -{Math.floor((dashboardStats.avgResponseTimeHours || parseFloat(dashboardStats.avgResponseTime) || 0) * 0.2)}%
              </span>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                <div
                  className="bg-orange-600 h-1.5 rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${Math.max(10, 100 - ((dashboardStats.avgResponseTimeHours || parseFloat(dashboardStats.avgResponseTime) || 0) * 10))}%` }}
                ></div>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                faster than avg
              </span>
            </div>
          </div>

          <div className={`bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 hover:shadow-md transition-all duration-300 p-5 smooth-card-hover ${animateStats ? 'smooth-fadeIn smooth-delay-400' : 'opacity-0'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Customer Satisfaction</h3>
              <div className="p-2 rounded-full bg-yellow-100 dark:bg-yellow-900/30 hover-rotate">
                <Star className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
            <div className="flex items-baseline">
              <span className="text-3xl font-bold text-gray-900 dark:text-white animate-counter">
                {dashboardStats.avgRating.toFixed(1)}
              </span>
              <span className="ml-2 text-sm font-medium flex items-center text-yellow-600 dark:text-yellow-400">
                <Star className="h-3 w-3 mr-1 fill-current" />
                /5.0
              </span>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`h-3 w-3 ${
                      star <= Math.floor(dashboardStats.avgRating)
                        ? 'text-yellow-400 fill-current'
                        : 'text-gray-300 dark:text-gray-600'
                    }`}
                  />
                ))}
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                +{Math.floor(dashboardStats.avgRating * 5)}% satisfaction
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Overview Tab Content */}
      {activeTab === "overview" && !isLoading && productAnalytics && (
        <>
          {/* Quick Insights Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-4">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{productAnalytics.totalProducts}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Total Products</div>
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-4">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{productAnalytics.topPerformers?.length || 0}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Top Performers</div>
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-4">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{productAnalytics.avgProductRating.toFixed(1)}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Avg Product Rating</div>
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-4">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{productAnalytics.products.reduce((sum, p) => sum + p.customerCount, 0)}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Total Customers</div>
            </div>
          </div>
        </>
      )}

      {/* Products Tab Content */}
      {activeTab === "products" && !isLoading && productAnalytics && (
        <>
          {/* Top Products Performance */}
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 p-5 mb-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Top Products by Reviews</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Products ranked by customer engagement</p>
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {productAnalytics.totalProducts} total products
              </div>
            </div>

            <div className="space-y-4">
              {productAnalytics.products.slice(0, 8).map((product: any, index: number) => (
                <div key={product.name} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full text-sm font-medium text-gray-600 dark:text-gray-400">
                      {index + 1}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white truncate max-w-xs">
                        {product.name}
                      </h4>
                      <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                        <span>{product.customerCount} customers</span>
                        <span>{product.platformCount} platforms</span>
                        {product.avgRating > 0 && (
                          <div className="flex items-center gap-1">
                            <Star className="h-3 w-3 fill-current text-yellow-400" />
                            <span>{product.avgRating}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                      {product.reviewCount}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">reviews</div>
                    {product.recentReviews > 0 && (
                      <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                        +{product.recentReviews} this week
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Platform Distribution */}
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 p-5">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Platform Distribution</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Reviews across platforms</p>
                </div>
              </div>
              <div className="h-80 flex items-center justify-center">
                <DonutChart
                  data={dashboardStats?.platformBreakdown ? Object.entries(dashboardStats.platformBreakdown).map(([platform, data]: [string, any], index) => ({
                    label: platform.charAt(0).toUpperCase() + platform.slice(1),
                    value: data.total || data.count || 0,
                    color: ['#111827', '#1F2937', '#374151', '#4B5563', '#6B7280', '#9CA3AF'][index % 6]
                  })) : undefined}
                  title="Platform Distribution"
                  centerText="Total Reviews"
                  centerValue={dashboardStats?.totalReviews?.toLocaleString() || "0"}
                />
              </div>
              {dashboardStats?.platformBreakdown && (
                <div className="grid grid-cols-2 gap-4 mt-4">
                  {Object.entries(dashboardStats.platformBreakdown).map(([platform, data]: [string, any]) => (
                    <div key={platform} className="text-center">
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">{data.total}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">{platform}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Response Performance */}
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 p-5">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Response Performance</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Review status breakdown</p>
                </div>
              </div>
              <div className="h-80">
                {productAnalytics?.products ? (
                  <ProductChart data={productAnalytics.products} type="horizontal-bar" height={300} />
                ) : (
                  <AreaChart />
                )}
              </div>
              {dashboardStats?.statusBreakdown && (
                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                      {dashboardStats.statusBreakdown.sent}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Sent</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                      {dashboardStats.statusBreakdown.draft}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Draft</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                      {dashboardStats.statusBreakdown.pending}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Pending</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Engagement Tab Content */}
      {activeTab === "engagement" && !isLoading && productAnalytics && (
        <>
          {/* Customer Engagement Metrics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 p-5">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Most Engaging Products</h3>
              <div className="space-y-4">
                {productAnalytics.products.slice(0, 8).map((product: any, index: number) => (
                  <div key={product.name} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-sm font-medium text-blue-600 dark:text-blue-400">
                        {index + 1}
                      </div>
                      <span className="font-medium text-gray-900 dark:text-white">{product.name}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">{product.customerCount}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">customers</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 p-5">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Review Frequency</h3>
              <div className="space-y-4">
                {productAnalytics.products.slice(0, 8).map((product: any, index: number) => (
                  <div key={product.name} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center text-sm font-medium text-green-600 dark:text-green-400">
                        {index + 1}
                      </div>
                      <span className="font-medium text-gray-900 dark:text-white">{product.name}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">{product.reviewCount}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">reviews</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Performance Tab Content */}
      {activeTab === "performance" && !isLoading && productAnalytics && (
        <>
          {/* Performance Metrics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 p-5">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Response Time Performance</h3>
              <div className="space-y-4">
                {productAnalytics.products.filter(p => p.avgResponseTime > 0).slice(0, 8).map((product: any, index: number) => (
                  <div key={product.name} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center text-sm font-medium text-orange-600 dark:text-orange-400">
                        {index + 1}
                      </div>
                      <span className="font-medium text-gray-900 dark:text-white">{product.name}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">{product.avgResponseTime}h</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">avg response</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 p-5">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Rating Performance</h3>
              <div className="space-y-4">
                {productAnalytics.products.filter(p => p.avgRating > 0).slice(0, 8).map((product: any, index: number) => (
                  <div key={product.name} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center text-sm font-medium text-yellow-600 dark:text-yellow-400">
                        {index + 1}
                      </div>
                      <span className="font-medium text-gray-900 dark:text-white">{product.name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 fill-current text-yellow-400" />
                      <span className="text-lg font-semibold text-gray-900 dark:text-white">{product.avgRating}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Product Performance Table */}
      {!isLoading && productAnalytics && (
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 p-5 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Product Performance Details</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Comprehensive product analytics</p>
            </div>
            <button className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
              View All Products
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-800">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Product</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Reviews</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Customers</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Avg Rating</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Response Time</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Platforms</th>
                </tr>
              </thead>
              <tbody>
                {productAnalytics.products.slice(0, 10).map((product: any, index: number) => (
                  <tr key={product.name} className="border-b border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded text-xs font-medium text-gray-600 dark:text-gray-400">
                          {index + 1}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white text-sm truncate max-w-xs">
                            {product.name}
                          </div>
                          {product.recentReviews > 0 && (
                            <div className="text-xs text-green-600 dark:text-green-400">
                              +{product.recentReviews} this week
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white">
                      {product.reviewCount}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                      {product.customerCount}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {product.avgRating > 0 ? (
                        <div className="flex items-center gap-1">
                          <Star className="h-3 w-3 fill-current text-yellow-400" />
                          <span className="text-gray-900 dark:text-white">{product.avgRating}</span>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                      {product.avgResponseTime > 0 ? `${product.avgResponseTime}h` : '-'}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <div className="flex items-center gap-1">
                        <span className="text-gray-900 dark:text-white">{product.platformCount}</span>
                        <span className="text-gray-500 dark:text-gray-400">platforms</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Additional Analytics Grid */}
      {!isLoading && productAnalytics && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Top Performers */}
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Top Performers</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Highest rated products</p>
              </div>
              <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/30">
                <Star className="h-4 w-4 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <div className="space-y-3">
              {productAnalytics.topPerformers?.slice(0, 5).map((product: any, index: number) => (
                <div key={product.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center text-xs font-medium text-green-600 dark:text-green-400">
                      {index + 1}
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[120px]">
                      {product.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Star className="h-3 w-3 fill-current text-yellow-400" />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{product.avgRating}</span>
                  </div>
                </div>
              )) || (
                <div className="text-center text-gray-500 dark:text-gray-400 py-4">
                  <Star className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No top performers yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Customer Engagement */}
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Customer Engagement</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Most engaging products</p>
              </div>
              <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/30">
                <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <div className="space-y-3">
              {productAnalytics.products.slice(0, 5).map((product: any, index: number) => (
                <div key={product.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-xs font-medium text-blue-600 dark:text-blue-400">
                      {index + 1}
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[120px]">
                      {product.name}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{product.customerCount}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">customers</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activity Summary */}
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Last 7 days</p>
              </div>
              <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-800">
                <Activity className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              </div>
            </div>
            {dashboardStats && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">New Reviews</span>
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">
                    {dashboardStats.recentActivity.newReviews}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Responses Generated</span>
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">
                    {dashboardStats.recentActivity.responsesGenerated}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Responses Sent</span>
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">
                    {dashboardStats.recentActivity.responsesSent}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Coming Soon Section */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
        <div className="max-w-2xl mx-auto">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full mb-4">
              <BarChart2 className="h-8 w-8 text-gray-600 dark:text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">More Analytics Coming Soon</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              We're working on advanced analytics features to give you even deeper insights into your business performance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                <span className="font-medium text-gray-900 dark:text-white">Predictive Analytics</span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                AI-powered insights to predict customer behavior and product trends
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-2">
                <Users className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                <span className="font-medium text-gray-900 dark:text-white">Customer Journey</span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Track customer interactions across all touchpoints and platforms
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-2">
                <MessageSquare className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                <span className="font-medium text-gray-900 dark:text-white">Sentiment Analysis</span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Advanced sentiment tracking and emotional intelligence insights
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-2">
                <Download className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                <span className="font-medium text-gray-900 dark:text-white">Custom Reports</span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Generate and export detailed reports with custom date ranges
              </p>
            </div>
          </div>

          <div className="text-sm text-gray-500 dark:text-gray-400">
            <p>🚀 <strong>Coming Q2 2025</strong> - Stay tuned for these exciting new features!</p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default function AnalyticsPage() {
  return (
    <ProtectedRoute requireOnboarding={true}>
      <AnalyticsContent />
    </ProtectedRoute>
  );
}